import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import store from '@/app/store';
import { loginSuccess, logout } from '@/features/Auth/authSlice';
import { env } from '@/shared/config/env';
import { getDeviceId, getPlatformInfo } from '@/shared/utils/platform';
import { handleSessionExpired } from '@/shared/utils/sessionHandler';
import { refreshTokenApi } from '@/features/Auth/api/authService';
import type { AuthResponse, RefreshTokenPayload } from '@/features/Auth/types';

const axiosInstance = axios.create({
  baseURL: env.api.baseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use((config) => {
  const token = store.getState()?.auth?.token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Token refresh coordination
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  config: AxiosRequestConfig & { _retry?: boolean };
}> = [];

const processQueue = (error: unknown, token?: string) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
      return;
    }
    const updatedConfig = { ...config };
    updatedConfig.headers = { ...(updatedConfig.headers || {}), Authorization: `Bearer ${token}` };
    resolve(axiosInstance(updatedConfig));
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = (error.config || {}) as AxiosRequestConfig & { _retry?: boolean };

    const status = error.response?.status;
    // ✅ If refresh token request itself failed
    if (originalRequest?.url?.includes("/refreshToken")) {
      handleSessionExpired();
      processQueue(error); // reject all queued requests
      return Promise.reject(error);
    }

    if (status !== 401) {
      return Promise.reject(error);
    }

    // Avoid infinite loop
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    const state = store.getState();
    const refreshToken = state?.auth?.refreshToken;

    if (!refreshToken) {
      store.dispatch(logout());
      return Promise.reject(error);
    }

    // Queue requests while a refresh is in-flight
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Build refresh payload consistent with existing API
      const deviceId = getDeviceId();
      const platform = getPlatformInfo().browser || '';
      const userId = state?.auth?.user?.authUserId ?? 0;

      const payload: RefreshTokenPayload = { refreshToken, platform, deviceId, userId };
      const response = await refreshTokenApi(payload);
      if (!response?.success || response?.data?.isExpired == "1") {
        handleSessionExpired();
        throw new Error(response?.data?.message || 'Refresh token failed');
      }

      // Update Redux state with new tokens
      store.dispatch(loginSuccess(response as AuthResponse));

      // Update header and retry original request
      const newToken = response.access_token as string;
      processQueue(null, newToken);

      const retryConfig = { ...originalRequest };
      retryConfig.headers = { ...(retryConfig.headers || {}), Authorization: `Bearer ${newToken}` };

      return axiosInstance(retryConfig);
    } catch (refreshErr) {
      processQueue(refreshErr);
      store.dispatch(logout());
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;
